import { useState, useCallback, useEffect } from "react";
import { Chip, Box, alpha } from "@mui/material";
import Swal from "sweetalert2";
import { getPermissionData, getUserData } from "../../Actions/common";
import {
  getPlanFeatureListAction,
  updatePlanFeatureAction,
  deletePlanFeatureAction,
} from "../../Actions/PlanFeatureAction";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import CustomSnackbar from "../Common/snackBar";
import AddEditPlanFeature from "./AddEditPlanFeature";
import renderStatusBadge from "../../utils/badges";
import DataTable from "../../components/DataTable/DataTable";

const PORTAL_BADGE_MAP = {
  qbot: { label: "QBot", color: "primary" },
  paycentral: { label: "PayCentral", color: "success" },
  kyc: { label: "KYC", color: "warning" },
  tripola: { label: "Tripolaa", color: "secondary" },
  tripolaa: { label: "Tripolaa", color: "secondary" },
};

const PlanFeatureManagementListing = () => {
  const [featuresListData, setFeaturesListData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [shouldFetchData, setShouldFetchData] = useState(1);
  const [selectedRowsPerPage, setSelectedRowsPerPage] = useState(
    ITEMS_PER_PAGE_OPTIONS[0]
  );
  const [currentId, setCurrentId] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [filterValues, setFilterValues] = useState({
    search: "",
    status: "all",
    portal: "all",
  });

  // Example permission checking
  const userData = localStorage.getItem("userData") ? getUserData() : null;
  const permissionData = localStorage.getItem(`${userData?.id}_permissionData`)
    ? getPermissionData(`${userData?.id}_permissionData`)
    : [];
  const hasPermission = permissionData[0]?.role_permissions?.find(
    (m) => m?.module && m?.module?.slug === "plan-feature-management"
  ) || { canEdit: true, canDelete: true, canPublish: true, canCreate: true }; // Defaulting to true for demo

  const getFeaturesList = useCallback(async () => {
    setLoading(true);

    try {
      const params = {
        page: currentPage,
        limit: selectedRowsPerPage,
        search: filterValues.search,
        parent_id: "null", // Only fetch top-level features
      };

      if (filterValues.status !== "all") {
        params.status = filterValues.status === "1" ? "true" : "false";
      }

      if (filterValues.portal !== "all") {
        params.portal_name = filterValues.portal;
      }

      const res = await getPlanFeatureListAction(params);

      const dataArray = Array.isArray(res?.data?.data) ? res.data.data : [];
      setFeaturesListData(dataArray);
      setTotalRecords(res?.data?.meta?.total || 0);
    } catch (error) {
      const msg = error?.data?.message || error?.message || "Something went wrong";
      setSnackbar({
        open: true,
        message: msg,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedRowsPerPage, filterValues]);

  useEffect(() => {
    if (shouldFetchData) {
      getFeaturesList();
      setShouldFetchData(0);
    }
  }, [shouldFetchData, getFeaturesList]);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleToggleFeatureStatus = async (feature) => {
    const newStatus = !feature.is_active;
    const actionText = newStatus ? "Activate" : "Deactivate";

    const result = await Swal.fire({
      title: `${actionText} Feature?`,
      text: `Are you sure you want to ${actionText.toLowerCase()} the feature "${feature.feature_name}"?`,
      icon: newStatus ? "question" : "warning",
      showCancelButton: true,
      confirmButtonColor: newStatus ? "#28a745" : "#dc3545",
      cancelButtonColor: "#777777",
      confirmButtonText: `Yes, ${actionText}`,
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const payload = {
          is_active: newStatus,
        };
        const res = await updatePlanFeatureAction(feature.id, payload);

        setSnackbar({
          open: true,
          message:
            res.message || `Feature ${actionText.toLowerCase()}d successfully`,
          severity: "success",
        });

        getFeaturesList();
      } catch (error) {
        setSnackbar({
          open: true,
          message: error?.message || "Failed to update feature status",
          severity: "error",
        });
      }
    }
  };

  const handleCreateFeature = (id = null) => {
    setCurrentId(id);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCurrentId(null);
  };

  const handleDeleteFeature = async (feature) => {
    Swal.fire({
      title: "Are you sure you want to delete?",
      text: `Permanently delete ${feature.feature_name} and its sub-features?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#777777",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        const response = await deletePlanFeatureAction(feature.id);

        Swal.fire({
          title: "Deleted!",
          text: response?.message || "Deleted successfully",
          icon: "success",
        });

        getFeaturesList();
      } catch (error) {
        setSnackbar({
          open: true,
          message: error?.message || "Delete failed",
          severity: "error",
        });
      }
    });
  };

  const handleAction = (type, row) => {
    switch (type) {
      case "edit":
        handleCreateFeature(row.id);
        break;
      case "delete":
        handleDeleteFeature(row);
        break;
      case "activate":
      case "deactivate":
        handleToggleFeatureStatus(row);
        break;
      default:
        break;
    }
  };

  const filters = [
    {
      name: "search",
      label: "Search",
      type: "search",
      placeholder: "Search Feature",
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      defaultValue: "all",
      options: [
        { label: "All Status", value: "all" },
        { label: "Active", value: "1" },
        { label: "Inactive", value: "0" },
      ],
    },
    {
      name: "portal",
      label: "Portal Name",
      type: "select",
      defaultValue: "all",
      options: [
        { label: "All Portals", value: "all" },
        { label: "QBot", value: "qbot" },
        { label: "Tripola", value: "tripolaa" },
        { label: "KYC", value: "kyc" },
      ],
    },
  ];

  const columns = [
    {
      field: "portal_name",
      headerName: "Portal Name",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => {
        const raw = String(params.row.portal_name || "").trim();
        if (!raw) return <span style={{ fontSize: 15, color: "#999" }}>—</span>;

        const portal =
          PORTAL_BADGE_MAP[raw.toLowerCase()] || {
            label: raw,
            color: "primary",
          };

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Chip
              label={portal.label}
              size="small"
              sx={(theme) => {
                const colorMap = {
                  primary: theme.palette.primary.main,
                  success: theme.palette.success.main,
                  warning: theme.palette.warning.main,
                  secondary: theme.palette.secondary.main,
                };
                const color = colorMap[portal.color] || theme.palette.primary.main;

                return {
                  height: 26,
                  fontWeight: 600,
                  fontSize: 11.5,
                  borderRadius: "8px",
                  color,
                  backgroundColor: alpha(color, 0.12),
                  border: `1px solid ${alpha(color, 0.25)}`,
                  textTransform: "none",
                  "& .MuiChip-label": { px: 1.2 },
                };
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "feature_name",
      headerName: "Feature Name",
      flex: 2,
      minWidth: 180,
      renderCell: (params) => (
        <span style={{ fontSize: 15, fontWeight: 500, color: "#333" }}>
          {params.row.feature_name || "N/A"}
        </span>
      ),
    },
    {
      field: "price",
      headerName: "Price",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <span style={{ fontSize: 15, fontWeight: 500, color: "#333" }}>
          {params.row.price !== undefined ? `$${params.row.price}` : "N/A"}
        </span>
      ),
    },
    {
      field: "is_active",
      headerName: "Status",
      flex: 1,
      minWidth: 130,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const statusMap = {
          true: { text: "Active", status: "success" },
          false: { text: "Inactive", status: "default" },
        };

        const statusCode = Boolean(params.row?.is_active);
        const { text, status } = statusMap[statusCode] || {
          text: "Unknown",
          status: "default",
        };

        return renderStatusBadge({ status, title: text });
      },
    },
  ];

  const actions = {
    edit: hasPermission?.canEdit,
    delete: hasPermission?.canDelete,
    activate: hasPermission?.canPublish,
    deactivate: hasPermission?.canPublish,
  };

  const handleFilterApply = (values) => {
    setFilterValues(values);
    setCurrentPage(1);
    setShouldFetchData(1);
  };

  const handleFilterReset = (clearedValues) => {
    setFilterValues(clearedValues);
    setCurrentPage(1);
    setShouldFetchData(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setShouldFetchData(1);
  };

  const handlePageSizeChange = (newSize) => {
    setSelectedRowsPerPage(newSize);
    setCurrentPage(1);
    setShouldFetchData(1);
  };

  return (
    <div id="kt_content_container">
      <BreadcrumbsComponent />

      <div className="card">
        <DataTable
          columns={columns}
          rows={featuresListData}
          loading={loading}
          totalCount={totalRecords}
          page={currentPage}
          pageSize={selectedRowsPerPage}
          filters={filters}
          filterValues={filterValues}
          actions={actions}
          statusField="is_active"
          onFilterApply={handleFilterApply}
          onFilterReset={handleFilterReset}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onAction={handleAction}
        />
      </div>

      <CustomSnackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        severity={snackbar.severity}
        message={snackbar.message}
      />

      {openModal && (
        <AddEditPlanFeature
          open={openModal}
          handleClose={handleCloseModal}
          id={currentId}
          refetchPlanList={() => {
            setShouldFetchData(1);
          }}
          handleToast={(severity, message) => {
            setSnackbar({
              open: true,
              severity,
              message,
            });
          }}
        />
      )}
    </div>
  );
};

export default PlanFeatureManagementListing;
